import type { CredentialItem } from "src/clients/agent-management";

export type CredentialProvider = "openai" | "google_vertex" | "anthropic";

export type CredentialEnvName = "live" | "sandbox";

export type OpenAIRegion = "global" | "us" | "eu";

export type EnvConfigInputs = {
  apiKey: string;
  region?: OpenAIRegion;
  /**
   * The stored base_url, carried verbatim so an update round-trips it
   * byte-identical unless the user explicitly changes the region.
   */
  baseUrl?: string;
  serviceAccountJson?: string;
  projectId?: string;
  location?: string;
};

export type AICredentialFormInputs = {
  provider: CredentialProvider;
  name: string;
  models: string[];
  envConfigs: Partial<Record<CredentialEnvName, EnvConfigInputs>>;
};

export const getDefaultEnvValues = (
  provider: CredentialProvider,
): EnvConfigInputs => ({
  apiKey: "",
  region: provider === "openai" ? "global" : undefined,
});

export type CredentialUpsertPayload = Record<string, unknown>;

export const REGION_TO_BASE_URL: Record<OpenAIRegion, string> = {
  global: "https://api.openai.com/v1",
  us: "https://us.api.openai.com/v1",
  eu: "https://eu.api.openai.com/v1",
};

const baseUrlToRegion = (baseUrl: string | undefined): OpenAIRegion => {
  const match = (
    Object.entries(REGION_TO_BASE_URL) as [OpenAIRegion, string][]
  ).find(([, url]) => url === baseUrl);
  return match?.[0] ?? "global";
};

export const getDefaultValues = (
  provider: CredentialProvider,
): AICredentialFormInputs => ({
  provider,
  name: "",
  models: [],
  envConfigs: {},
});

type BEEnvConfig = {
  base_url?: string;
  project_id?: string;
  location?: string;
};

const convertBEEnvConfigToInputs = (
  provider: string,
  config: BEEnvConfig,
): EnvConfigInputs => ({
  apiKey: "",
  region: provider === "openai" ? baseUrlToRegion(config.base_url) : undefined,
  baseUrl: provider === "openai" ? config.base_url : undefined,
  projectId: config.project_id,
  location: config.location,
});

export const convertBECredentialToFormInputs = (
  item: CredentialItem,
): AICredentialFormInputs => {
  const envConfigs: Partial<Record<CredentialEnvName, EnvConfigInputs>> =
    Object.fromEntries(
      Object.entries(item.env_configs ?? {}).map(([env, config]) => [
        env,
        convertBEEnvConfigToInputs(item.provider, config ?? {}),
      ]),
    );
  return {
    provider: item.provider as CredentialProvider,
    name: item.name,
    models: item.models ?? [],
    envConfigs,
  };
};

const convertEnvInputsToBEPayload = (
  provider: CredentialProvider,
  env: EnvConfigInputs,
): Record<string, unknown> => {
  switch (provider) {
    case "openai":
      return {
        api_key: env.apiKey,
        // Prefer the carried-over stored base_url so an untouched region
        // round-trips byte-identical (the backend allowlist accepts variants
        // such as trailing slashes that the region map would clobber). The
        // region-derived URL is only used on create or after an explicit
        // region change (which overwrites baseUrl).
        base_url: env.baseUrl ?? REGION_TO_BASE_URL[env.region ?? "global"],
      };
    case "google_vertex":
      return {
        service_account_json: env.serviceAccountJson!,
        // Non-secret fields are always sent (the backend requires them on every
        // env entry): an explicit null clears the stored value, a value sets it.
        project_id: env.projectId?.trim() || null,
        location: env.location?.trim() || null,
      };
    case "anthropic":
      return { api_key: env.apiKey };
  }
};

export const convertFormInputsToBECredential = (
  form: AICredentialFormInputs,
): CredentialUpsertPayload => {
  const envConfigs: Record<string, unknown> = Object.fromEntries(
    Object.entries(form.envConfigs).flatMap(([env, config]) =>
      config ? [[env, convertEnvInputsToBEPayload(form.provider, config)]] : [],
    ),
  );
  return {
    name: form.name,
    models: form.models,
    // Always false from this UI — users add a dedicated sandbox env instead
    // of relying on the production-key fallback.
    allow_test_invocations: false,
    env_configs: envConfigs,
  };
};

const SECRET_PAYLOAD_FIELDS = ["api_key", "service_account_json"] as const;

const omitFields = (
  target: Record<string, unknown>,
  fields: readonly string[],
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(target).filter(([key]) => !fields.includes(key)),
  );

/**
 * PATCH semantics for an update payload: a secret the user did not edit is
 * omitted entirely so the backend leaves the stored value untouched. Only the
 * secret fields are dropped — non-sensitive config (base_url, region, etc.) is
 * still sent. Applies per environment.
 */
export const omitUneditedSecrets = (
  payload: CredentialUpsertPayload,
  edited: Record<CredentialEnvName, boolean>,
): CredentialUpsertPayload => {
  const envConfigs = payload.env_configs as
    | Record<CredentialEnvName, Record<string, unknown>>
    | undefined;
  if (!envConfigs) return payload;

  return {
    ...payload,
    env_configs: Object.fromEntries(
      Object.entries(envConfigs).map(([env, config]) => [
        env,
        edited[env as CredentialEnvName]
          ? config
          : omitFields(config, SECRET_PAYLOAD_FIELDS),
      ]),
    ),
  };
};
